import { fill, omitBy } from 'lodash';
import * as Yup from 'yup';

export const luggageTypeRegex = /^luggage-type-(\d+)/;

const getLuggageCount = (value) =>
  Object.keys(value || {}).reduce((accumulator, key) => {
    if (luggageTypeRegex.test(key)) {
      return accumulator + value[key];
    }
    return accumulator;
  }, 0);

export function hasLuggage(value) {
  return getLuggageCount(value) > 0;
}

export const luggageFormSchema = Yup.object().shape({
  confirmLuggageDetails: Yup.boolean().when('needLuggageFreightTc', {
    is: true,
    then: (schema) => schema.oneOf([true]).required(),
  }),
  confirmBikeHireTc: Yup.boolean().when('needBikeHireTc', {
    is: true,
    then: (schema) => schema.oneOf([true]).required(),
  }),
});

export const getFormValuesToSubmit = (formValues) => {
  const keysToRemove = [
    'confirmLuggageDetails',
    'confirmBikeHireTc',
    'needConfirm',
    'needLuggageFreightTc',
    'needBikeHireTc',
  ];

  return omitBy({ ...formValues, submitted: true }, (v, k) =>
    keysToRemove.includes(k)
  );
};

/**
 * Extract QT luggage type IDs from categories matching a filter condition
 */
const getLuggageTypeIdsByFilter = (
  luggageCategories,
  luggageGroups,
  luggageTypes,
  filterFn
) => {
  if (!luggageCategories || !luggageGroups || !luggageTypes) {
    return [];
  }

  const filteredCategories = luggageCategories.filter(filterFn);

  return filteredCategories.reduce((allTypeIds, category) => {
    const groupIds = category.luggageGroups || [];
    const groups = groupIds.map((id) => luggageGroups[id]).filter(Boolean);

    const typeIds = groups.reduce((groupTypeIds, group) => {
      const types = (group.luggageTypes || [])
        .map((id) => luggageTypes[id])
        .filter(Boolean);
      const qtIds = types
        .map((type) => type.qtLuggageTypeId)
        .filter((id) => id != null);
      return groupTypeIds.concat(qtIds);
    }, []);

    return allTypeIds.concat(typeIds);
  }, []);
};

/**
 * Get QT luggage type IDs for categories with showBikeHireTc: true
 */
export const getBikeHireLuggageTypeIds = (
  luggageCategories,
  luggageGroups,
  luggageTypes
) =>
  getLuggageTypeIdsByFilter(
    luggageCategories,
    luggageGroups,
    luggageTypes,
    (cat) => cat.showBikeHireTc
  );

/**
 * Get QT luggage type IDs for categories with showBikeHireTc: false (regular luggage/freight)
 */
export const getLuggageFreightTypeIds = (
  luggageCategories,
  luggageGroups,
  luggageTypes
) =>
  getLuggageTypeIdsByFilter(
    luggageCategories,
    luggageGroups,
    luggageTypes,
    (cat) => !cat.showBikeHireTc
  );

/**
 * Check if any luggage of a specific type is selected
 */
export const hasLuggageOfType = (formValues, typeIds) => {
  if (!formValues || !typeIds?.length) {
    return false;
  }

  return typeIds.some((qtId) => {
    const fieldValue = formValues[`luggage-type-${qtId}`];
    return fieldValue && fieldValue > 0;
  });
};

export const transferLuggageFormToArray = (formValues) =>
  Object.keys(formValues).reduce((accumulator, key) => {
    const count = formValues[key];
    if (luggageTypeRegex.test(key) && count > 0) {
      const id = Number(key.match(luggageTypeRegex)[1]);
      const arr = new Array(count);
      fill(arr, { vehicle_type_id: id });
      return arr.concat(accumulator);
    }
    return accumulator;
  }, []);

export const getRelatedLuggageTypesFunc =
  ({ luggageGroups, luggageTypes }) =>
  (groupIds) => {
    const groups = groupIds.map((id) => luggageGroups[id]);
    return groups.reduce((qtIdsOfAll, currentGroup) => {
      const types = currentGroup.luggageTypes.map((id) => luggageTypes[id]);
      const typeQtIds = types.reduce(
        (qtIds, type) => qtIds.concat(type.qtLuggageTypeId),
        []
      );
      return qtIdsOfAll.concat(typeQtIds);
    }, []);
  };

/**
 * Get all QT luggage type IDs for categories with onlyForSameDayReturn: true
 * Used to identify which luggage items need to be removed when dates change from same-day to different-day return
 *
 * @param {Array} sameDayCategories - Pre-filtered categories with onlyForSameDayReturn: true
 * @param {Object} luggageGroups - Luggage groups keyed by ID
 * @param {Object} luggageTypes - Luggage types keyed by ID
 * @returns {Array<number>} Array of QT luggage type IDs
 */
export const getSameDayLuggageTypeIds = (
  sameDayCategories,
  luggageGroups,
  luggageTypes
) =>
  getLuggageTypeIdsByFilter(
    sameDayCategories,
    luggageGroups,
    luggageTypes,
    () => true // Categories are pre-filtered by caller
  );

/**
 * Check if two dates are on the same day
 * @param {string} departureDate - ISO date string (e.g., "2022-05-18T14:30:00.000Z")
 * @param {string} returnDate - ISO date string
 * @returns {boolean} True if both dates are on the same calendar day
 */
export const isSameDayDates = (departureDate, returnDate) => {
  // Validate inputs are non-empty strings
  if (
    !departureDate ||
    !returnDate ||
    typeof departureDate !== 'string' ||
    typeof returnDate !== 'string'
  ) {
    return false;
  }

  // Handle both ISO format (with T) and date-only format (YYYY-MM-DD)
  const departureDatePart = departureDate.includes('T')
    ? departureDate.split('T')[0]
    : departureDate;
  const returnDatePart = returnDate.includes('T')
    ? returnDate.split('T')[0]
    : returnDate;

  return departureDatePart === returnDatePart;
};
